/**
 * NutritionalFingerprint.jsx — iter853 (Feb 2026)
 *
 * 5-axis radar visualising the patient's 14d meal-log signature on:
 *   NIS · DIL · UIS · Col3 · PRAL
 *
 * Uses recharts RadarChart. Draws:
 *   · target ring (green)
 *   · harm threshold ring (amber dashed)
 *   · patient locus polygon (band-coloured)
 *   · axis status pills below the chart
 *
 * Truthful Patterns: when meals_count_14d < 3, renders an honest empty
 * state instead of a fabricated polygon.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Tooltip,
} from "recharts";
import { Sparkles, AlertTriangle, RefreshCw } from "lucide-react";
import { useAuth } from "@/App";

const TX = {
  bg: {
    title: "Хранителен отпечатък",
    subtitle: "5-осна 14-дневна сигнатура (реални данни от дневника)",
    empty_title: "Няма достатъчно данни",
    empty_body: "Записвайте поне 3 хранения през последните 14 дни, за да видите Вашия отпечатък.",
    composite: "Общо съответствие",
    axis_value: "Стойност",
    axis_target: "Оптимален target",
    axis_harm: "Вреден праг",
    axis_higher_better: "по-високо е по-добре",
    axis_lower_better: "по-ниско е по-добре",
    band: {
      green: "Оптимално", yellow: "Наблюдение", orange: "Внимание",
      red: "Приоритет", insufficient_data: "Няма данни",
    },
    refresh: "Обнови",
    disclaimer: "Изчислено от последните 14 дни на дневника Ви. Не е медицинска препоръка.",
  },
  en: {
    title: "Nutritional fingerprint",
    subtitle: "5-axis 14-day signature (real meal log)",
    empty_title: "Not enough data yet",
    empty_body: "Log at least 3 meals in the past 14 days to see your fingerprint.",
    composite: "Overall alignment",
    axis_value: "Value",
    axis_target: "Optimal target",
    axis_harm: "Harm threshold",
    axis_higher_better: "higher = better",
    axis_lower_better: "lower = better",
    band: {
      green: "Optimal", yellow: "Watch", orange: "Attention",
      red: "Priority", insufficient_data: "No data",
    },
    refresh: "Refresh",
    disclaimer: "Computed from your last 14 days of meal log. Not medical advice.",
  },
};

const BAND_COLOR = {
  green:  "#10b981",
  yellow: "#f59e0b",
  orange: "#f97316",
  red:    "#ef4444",
  insufficient_data: "#94a3b8",
};

/**
 * Normalise each axis to a 0-100 scale where 100 = optimal.
 *   - For lower_is_better axes: 100 * (1 - value/harm), clamped 0..100
 *   - For higher_is_better axes (Col3): 100 * value/100, clamped 0..100
 */
function axisNorm(axis) {
  const v = Number(axis.value || 0);
  if (axis.band === "insufficient_data") return 0;
  if (axis.lower_is_better) {
    const harm = Math.max(1, Number(axis.harm || 100));
    return Math.max(0, Math.min(100, 100 * (1 - v / harm)));
  }
  return Math.max(0, Math.min(100, v));
}

function targetNorm(axis) {
  if (axis.lower_is_better) {
    const harm = Math.max(1, Number(axis.harm || 100));
    return Math.max(0, Math.min(100, 100 * (1 - axis.target / harm)));
  }
  return Math.max(0, Math.min(100, Number(axis.target || 0)));
}

export default function NutritionalFingerprint({ lang = "bg", isDark = false }) {
  const { API } = useAuth();
  const t = TX[lang] || TX.bg;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data: resp } = await axios.get(`${API}/breast-intelligence/nutritional-fingerprint`);
      setData(resp);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className={`rounded-2xl p-4 ${isDark ? "bg-slate-900/60" : "bg-white/70"} border ${isDark ? "border-slate-700" : "border-slate-200"}`}
           data-testid="nutritional-fingerprint-loading">
        <div className="animate-pulse h-64 rounded-xl bg-slate-200 dark:bg-slate-800" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="rounded-2xl p-4 border border-red-300 bg-red-50 text-red-900 dark:bg-red-500/10 dark:text-red-200"
           data-testid="nutritional-fingerprint-error">
        <div className="flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4" /> {String(err).slice(0, 200)}
        </div>
        <button onClick={load} className="mt-2 text-xs underline">{t.refresh}</button>
      </div>
    );
  }

  const insufficient = !!data?.insufficient_data;

  // Chart data: each axis normalised to 0..100 (patient) + target ring
  const chartData = (data?.axes || []).map((a) => ({
    axis:  lang === "en" ? a.name_en : a.name_bg,
    value: axisNorm(a),
    target: targetNorm(a),
    _raw: a,
  }));

  return (
    <div
      className={`rounded-2xl p-4 border ${isDark ? "bg-slate-900/60 border-slate-700" : "bg-white/70 border-slate-200"}`}
      data-testid="nutritional-fingerprint"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Sparkles className={`w-4 h-4 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
          <h4 className={`text-sm font-bold ${isDark ? "text-gray-100" : "text-gray-900"}`}>{t.title}</h4>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1 text-[10px] opacity-70 hover:opacity-100"
          data-testid="nutritional-fingerprint-refresh"
        >
          <RefreshCw className="w-3 h-3" /> {t.refresh}
        </button>
      </div>
      <div className="text-[10px] opacity-60 mb-3">{t.subtitle}</div>

      {insufficient ? (
        <div className="py-10 text-center" data-testid="nutritional-fingerprint-empty">
          <div className="text-sm font-bold opacity-80">{t.empty_title}</div>
          <div className="text-[11px] opacity-60 mt-1">{t.empty_body}</div>
        </div>
      ) : (
        <>
          <div className="h-64 -mx-2" data-testid="nutritional-fingerprint-chart">
            <ResponsiveContainer>
              <RadarChart data={chartData} outerRadius="72%">
                <PolarGrid stroke={isDark ? "#334155" : "#cbd5e1"} />
                <PolarAngleAxis
                  dataKey="axis"
                  tick={{ fill: isDark ? "#cbd5e1" : "#475569", fontSize: 10 }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 100]}
                  tick={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#0f172a" : "#ffffff",
                    border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
                    borderRadius: 8,
                    fontSize: 11,
                  }}
                  formatter={(val, key, props) => {
                    const a = props?.payload?._raw;
                    if (!a) return [val, key];
                    const dir = a.lower_is_better ? t.axis_lower_better : t.axis_higher_better;
                    return [
                      `${a.value} (${dir}) · target ${a.target} · harm ${a.harm}`,
                      lang === "en" ? a.name_en : a.name_bg,
                    ];
                  }}
                />
                <Radar
                  name="target"
                  dataKey="target"
                  stroke="#10b981"
                  strokeDasharray="4 3"
                  fill="#10b981"
                  fillOpacity={0.10}
                />
                <Radar
                  name="value"
                  dataKey="value"
                  stroke={BAND_COLOR[data?.overall_band] || "#6366f1"}
                  fill={BAND_COLOR[data?.overall_band] || "#6366f1"}
                  fillOpacity={0.35}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Composite badge */}
          <div className="flex items-center justify-between px-1 mt-1">
            <span className="text-[10px] opacity-60">{t.composite}</span>
            <span
              className="px-2 py-0.5 rounded-full text-[11px] font-bold text-white"
              style={{ backgroundColor: BAND_COLOR[data?.overall_band] || "#94a3b8" }}
              data-testid="fingerprint-composite-badge"
            >
              {data?.composite}/100 · {t.band[data?.overall_band] || "—"}
            </span>
          </div>

          {/* Per-axis status pills */}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {(data?.axes || []).map((a) => (
              <div
                key={a.id}
                className={`flex items-center justify-between text-[10px] rounded-lg px-2 py-1 ${
                  isDark ? "bg-slate-800/60" : "bg-slate-100"
                }`}
                data-testid={`fingerprint-axis-${a.id}`}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: BAND_COLOR[a.band] }}
                  />
                  <span className="truncate">{lang === "en" ? a.name_en : a.name_bg}</span>
                </div>
                <span className="font-semibold shrink-0 ml-1">
                  {a.value} · {lang === "en" ? a.status_en : a.status_bg}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="text-[9px] italic opacity-50 mt-3">{t.disclaimer}</div>
    </div>
  );
}
